import React from 'react';
import PropTypes from 'prop-types';

import pluralize from 'pluralize';
import AsyncSelect from 'react-select/async';
import classNames from 'classnames';
import filter from 'lodash/filter';

import ContactAndDatabaseOption from './associated_contact_selector/options/contact_and_database';
import ContactOption from './associated_contact_selector/options/contact';
import { debouncedSearchContacts, debouncedSearchCrmContacts } from 'components/contacts/api';

import 'styles/entity_selector.scss';

export default class AssociatedContactSelector extends React.Component {
  static propTypes = {
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    createText: PropTypes.string,
    // Search the CRM (backoffice_contacts) instead of the legacy contact databases. `limitToType`
    // does not apply in this mode - `type` narrows the results server-side instead.
    crmSearch: PropTypes.bool,
    databases: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired
    })),
    disabled: PropTypes.bool,
    limitToType: PropTypes.string,
    projectId: PropTypes.number,
    selectedValue: PropTypes.object,
    styles: PropTypes.object,
    type: PropTypes.oneOf(['Contacts::Company', 'Contacts::Person']),
    typeLabel: PropTypes.string,
    onCreateContact: PropTypes.func,
    onModelAdded: PropTypes.func.isRequired
  };

  state = {
    value: ''
  }

  typeLabel = this.props.typeLabel || (this.props.type == 'Contacts::Company' ? 'Company' : 'Contact');

  searchContacts(term, callback) {
    const params = { term, projectId: this.props.projectId };
    if (this.props.databases) params.databases = this.props.databases;
    if (this.props.type) {
      params.type = this.props.type;
    }

    debouncedSearchContacts(params, (contacts) => {
      const options = contacts.map((contact) => {
        return {
          ...contact,
          value: contact.id,
          label: this.labelForContact(contact),
          type: contact.contactType,
          database: contact.databaseName
        };
      });

      callback(this.filterOptionsByType(options));
    });
  }

  labelForContact = (contact) => {
    if (contact.name && contact.email) return `${contact.name} (${contact.email})`;

    return contact.name || contact.email;
  }

  searchCrmContacts(term, callback) {
    const params = { term };
    if (this.props.type) params.type = this.props.type;

    debouncedSearchCrmContacts(params, (contacts) => {
      callback(contacts.map(contact => this.crmOption(contact)));
    });
  }

  // A CRM search result is either a contact that lives in the CRM (`id` set) or a legacy database
  // contact surfaced through it (only `crmContactId`/`crmContactType` set). The legacy ones already
  // have a Contacts::Person/Company record, so a lead should point at that rather than the CRM copy.
  crmOption = (contact) => {
    const legacyBacked = !contact.id && !!contact.crmContactId;

    return {
      ...contact,
      database: contact.source === 'backoffice' ? 'CRM' : contact.source,
      label: this.labelForContact(contact),
      person: contact.contactType === 'person',
      type: legacyBacked ? contact.crmContactType : 'Backoffice::Contact',
      value: legacyBacked ? contact.crmContactId : contact.id
    };
  };

  searchForContacts = (inputValue, callback) => {
    if (this.props.crmSearch) {
      this.searchCrmContacts(inputValue, callback);
    } else {
      this.searchContacts(inputValue, callback);
    }
  };

  loadContactOptions = (inputValue, callback) => {
    if (this.props.onCreateContact) {
      this.loadContactOptionsWithCreate(inputValue, callback);
    } else {
      this.searchForContacts(inputValue, callback);
    }
  }

  loadContactOptionsWithCreate = (inputValue, callback) => {
    const createContact = {
      type: 'addContact',
      label: this.props.createText || `Create ${this.typeLabel}`,
      onClick: this.props.onCreateContact
    };

    if (inputValue) {
      this.searchForContacts(inputValue, (options) => {
        callback([...options, createContact]);
      });
    } else {
      callback([createContact]);
    }
  }

  filterOptionsByType = (options) => {
    if (!this.props.limitToType) return options;

    return filter(options, { type: this.props.limitToType });
  }

  onSelected = (selection) => {
    if (!selection.type || selection.type === 'addContact') return undefined;

    this.setState({ value: selection });

    const modelDetails = {
      ...selection,
      associatedModelId: selection.value,
      associatedModelType: selection.type
    };
    delete modelDetails.id;

    this.props.onModelAdded(modelDetails);
  }

  currentlySelectedValue = () => {
    if (!this.props.selectedValue) return;

    return {
      value: this.props.selectedValue.associatedModelId,
      label: this.props.selectedValue.name || this.props.selectedValue.email,
      type: this.props.selectedValue.associatedModelType
    };
  }

  render() {
    const Option = !this.props.databases || this.props.databases.length > 1 ? ContactAndDatabaseOption : ContactOption;

    return (
      <AsyncSelect
        autoFocus={this.props.autoFocus}
        className={classNames('entity-selector', this.props.className)}
        classNamePrefix=""
        components={{ Option }}
        defaultOptions={!!this.props.onCreateContact}
        isDisabled={this.props.disabled}
        loadOptions={this.loadContactOptions}
        placeholder={`Search ${pluralize(this.typeLabel)}...`}
        styles={this.props.styles}
        value={this.currentlySelectedValue()}
        onChange={this.onSelected}
      />
    );
  }
}
